import { Icon } from '@scale/scaleui';
import { useCurrentEditor } from '@tiptap/react';
import useIsEditorEditable from '../../hooks/useIsEditorEditable';
import { useCallback } from 'react';

export function TableButton() {
  const { editor } = useCurrentEditor();
  const isEditable = useIsEditorEditable();
  if (!editor || editor?.isActive('table')) return null;
  return (
    <div className="p-2">
      <Icon
        tooltip="Insert Table"
        tooltipProps={{
          delayDuration: 0,
        }}
        onClick={() =>
          editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
        }
        icon="table"
        size="sm"
        disabled={!isEditable}
      />
    </div>
  );
}

export function TableDropdown() {
  const { editor } = useCurrentEditor();
  const isEditable = useIsEditorEditable();

  const handleDeleteRow = useCallback(() => {
    if (!editor) return;
    if (editor.isActive('tableHeader')) {
      editor.chain().focus().deleteRow().toggleHeaderRow().run();
      return;
    }
    editor.chain().focus().deleteRow().run();
  }, [editor]);

  const handleAddRowBefore = useCallback(() => {
    if (!editor) return;
    if (editor.isActive('tableHeader')) {
      editor
        .chain()
        .toggleHeaderRow()
        .addRowBefore()
        .command(({ tr, chain }) => {
          // if a table is selected
          if (editor.isActive('table')) {
            // find the active table
            const { anchor } = tr.selection;
            let activeTable = 0;
            tr.doc.descendants((node, pos) => {
              if (node.type.name === 'table' && anchor >= pos && anchor <= pos + node.nodeSize) {
                activeTable = pos;
              }
            });
            // move the focus to the first cell and toggle the row as the header row
            chain()
              .focus(activeTable + 2)
              .toggleHeaderRow();
            return true;
          }
          return false;
        })
        .run();
      return;
    }

    editor.chain().focus().addRowBefore().run();
  }, [editor]);

  if (!editor || !isEditable || !editor.isActive('table')) return null;
  return (
    <>
      <div className="inline-block w-0.5 self-stretch bg-neutral-200"></div>
      <div className="p-2 text-xs flex items-center">Table:</div>
      <div className="p-2">
        <Icon
          tooltip="Delete Table"
          tooltipProps={{
            delayDuration: 0,
          }}
          onClick={() => editor.chain().focus().deleteTable().run()}
          icon="times"
          size="sm"
          disabled={!isEditable}
        />
      </div>

      <div className="p-2">
        <Icon
          tooltip="Add row before"
          tooltipProps={{
            delayDuration: 0,
          }}
          onClick={handleAddRowBefore}
          icon="diagram-previous"
          size="sm"
          disabled={!isEditable}
        />
      </div>
      <div className="p-2">
        <Icon
          tooltip="Add row after"
          tooltipProps={{
            delayDuration: 0,
          }}
          onClick={() => editor.chain().focus().addRowAfter().run()}
          icon="diagram-next"
          size="sm"
          disabled={!isEditable}
        />
      </div>
      <div className="p-2">
        <Icon
          tooltip="Delete row"
          tooltipProps={{
            delayDuration: 0,
          }}
          onClick={handleDeleteRow}
          icon="diagram-cells"
          size="sm"
          disabled={!isEditable}
        />
      </div>
      <div className="p-2">
        <Icon
          tooltip="Add column before"
          tooltipProps={{
            delayDuration: 0,
          }}
          onClick={() => editor.chain().focus().addColumnBefore().run()}
          icon="border-left"
          size="sm"
          disabled={!isEditable}
        />
      </div>
      <div className="p-2">
        <Icon
          tooltip="Add column after"
          tooltipProps={{
            delayDuration: 0,
          }}
          onClick={() => editor.chain().focus().addColumnAfter().run()}
          icon="border-right"
          size="sm"
          disabled={!isEditable}
        />
      </div>
      <div className="p-2">
        <Icon
          tooltip="Delete column"
          tooltipProps={{
            delayDuration: 0,
          }}
          onClick={() => editor.chain().focus().deleteColumn().run()}
          icon="border-none"
          size="sm"
          disabled={!isEditable}
        />
      </div>
      <div className="inline-block w-0.5 self-stretch bg-neutral-200"></div>
    </>
  );
}
